import { LegalEntityContactType } from "../legalEntity/legalEntityContactTab/LegalEntityContactTab.types";
import { setFirstLetterToLowerCase } from "../common/stringOperations";
import { AssigneeType, SelectableAssignee } from "./SelectableAssignee.types";

export function getAssigneeRole(assignee: SelectableAssignee, translateString: (key: string) => string) {
    if (assignee.assigneeType !== AssigneeType.LegalEntityContact) {
        return getAssigneeTypeName(assignee.assigneeType, translateString);
    }

    const roles = assignee.contactTypes
        .map((contactType) => translateString(setFirstLetterToLowerCase(LegalEntityContactType[contactType].toString())))
        .join(", ");

    // A contact carrying no roles would otherwise be the only recipient in the list that does not say what kind
    // it is, which reads as a mistake next to a platform user of the same name.
    return roles || getAssigneeTypeName(assignee.assigneeType, translateString);
}

// Deliberately not the generic "contact" key: that one also labels contacts on suppliers, where the group
// company qualifier would be false. Here the selectable contacts are scoped to group companies, and this is the
// one list where all three recipient kinds sit side by side and need telling apart.
export function getAssigneeTypeName(assigneeType: AssigneeType, translateString: (key: string) => string) {
    switch (assigneeType) {
        case AssigneeType.ExternalUser:
            return translateString("externalUser");
        case AssigneeType.User:
            return translateString("user");
        default:
            return translateString("groupCompanyContact");
    }
}

// A group company contact is named after where it comes from, so it explains itself; the other two are platform
// concepts a reader cannot tell apart from the label alone.
export function getAssigneeTypeTooltip(assigneeType: AssigneeType, translateString: (key: string) => string) {
    switch (assigneeType) {
        case AssigneeType.ExternalUser:
            return translateString("contactRecipientTypeTooltip");
        case AssigneeType.User:
            return translateString("userRecipientTypeTooltip");
        default:
            return undefined;
    }
}

export function getAssigneeLabel(assignee: SelectableAssignee, translateString: (key: string) => string) {
    return `${assignee.name} (${getAssigneeRole(assignee, translateString)})`;
}

export interface ChangeAssigneeRequest {
    assigneeId: string;
    assigneeType: AssigneeType;
}

// The type can only come from the selectable list, and the list is scoped to the legal entities the current user
// can see, so the assessment's existing assignee is not always in it. Returning undefined rather than a request
// with a missing type keeps the caller from sending one the backend has to reject.
export function getChangeAssigneeRequest(assignees: Array<SelectableAssignee> | undefined, assigneeId: string | undefined): ChangeAssigneeRequest | undefined {
    const assignee = assignees?.find((selectable) => selectable.id === assigneeId);

    return assignee ? { assigneeId: assignee.id, assigneeType: assignee.assigneeType } : undefined;
}
